import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import type { User } from '@core/account-types';
import { AdminsAvatarsComponent } from '@core/account-ui';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import type { Visibility } from '@core/visibility-types';
import { VisibilityLevel } from '@core/visibility-types';

import type { VisibilitySelectLabels } from './types';
import type { VisibilityInfoContext } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import { VisibilitySelectorDropdownComponent } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import type { RoleWithAdmins } from '../../types';

@Component({
  selector: 'rt-visibility-selector',
  templateUrl: 'visibility-selector.component.html',
  styleUrls: ['./visibility-selector.component.less'],
  host: {
    '[class.wrap-on-smaller-resolutions]': 'wrapOnSmallerResolutions()',
    '[class.editable]': 'editable()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IconComponent,
    RtTooltip,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    NgTemplateOutlet,
    ConnectedOverlayDirective,
    VisibilitySelectorDropdownComponent,
    AdminsAvatarsComponent,
    IconButtonComponent,
    TranslatePipe,
  ],
})
export class VisibilitySelectorComponent implements OnChanges {
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  public readonly i18n = inject(I18nService);

  public readonly admins = input<User[]>(undefined!);
  public readonly disallowedUsers = input<User[]>(undefined!);
  public readonly rolesWithAdmins = input<RoleWithAdmins[]>(undefined!);
  public readonly hasOnlyMeLevel = input<boolean>(true);
  public readonly hasSelectTeamMembers = input<boolean>(true);
  public readonly hasLabel = input<boolean>(true);
  public readonly hasLockIcon = input<boolean>(true);
  public readonly editable = input<boolean>(true);
  public readonly allowAdminsChange = input<boolean>(true);
  public readonly wrapOnSmallerResolutions = input<boolean>(false);
  public readonly avatarMaxLength = input<number>(3);
  public readonly infoContext = input<VisibilityInfoContext>('view');
  public readonly buttonVariant = input<'plain' | 'outline'>('plain');
  public readonly avatarSize = input<'small' | 'small-x'>('small');
  public readonly dropdownPositions = input<ConnectionPositionPair[]>(undefined!);
  public readonly dropdownOffsetX = input<number>(0);
  public readonly dropdownOffsetY = input<number>(0);
  @Input() public visibility: Visibility;
  @Input() public protectedUsersPosition: 'outside' | 'inside' = 'outside';
  @Input() public labels: VisibilitySelectLabels;

  public visibilityDropdownToggle = output<boolean>();
  public visibilityChanges = output<VisibilityLevel>();

  public dropdownOpened: boolean = false;

  public ngOnChanges(changes: SimpleChanges): void {
    const infoContext = this.infoContext();
    if (changes.infoContext && infoContext && !this.labels) {
      this.labels = this.getDefaultLabels(infoContext);
    }
  }

  public ngOnInit() {
    if (!this.labels) {
      this.labels = this.getDefaultLabels(this.infoContext());
    }
  }

  public onChangeVisibilityLevel(level: VisibilityLevel): void {
    this.visibilityChanges.emit(level);
    this.cdRef.detectChanges();
  }

  public buttonClick(): void {
    if (!this.editable()) {
      return;
    }
    this.toggleDropdown();
  }

  public toggleDropdown(): void {
    this.dropdownOpened = !this.dropdownOpened;
    this.visibilityDropdownToggle.emit(this.dropdownOpened);
    this.cdRef.detectChanges();
  }

  public isPrivate(): boolean {
    return [VisibilityLevel.Protected, VisibilityLevel.Private].includes(this.visibility.level);
  }

  public setProtectedLevel(): void {
    if (!this.editable() || !this.allowAdminsChange()) {
      return;
    }
    this.visibilityChanges.emit(VisibilityLevel.Protected);
  }

  private getDefaultLabels(context: VisibilityInfoContext): VisibilitySelectLabels {
    switch (context) {
      case 'access':
        return {
          selected: {
            protected: this.i18n.translate('app.visibility.access_by'),
            public: this.i18n.translate('app.visibility.access_by_everyone'),
            private: this.i18n.translate('app.visibility.access_by_me'),
          },
        };

      default:
      case 'view':
        return {
          selected: {
            protected: this.i18n.translate('app.visibility.selector.protected.value'),
            public: this.i18n.translate('app.visibility.selector.public.value'),
            private: this.i18n.translate('app.visibility.selector.private.value'),
          },
        };
    }
  }
}
